import React from 'react';
import ArrowTooltip, {ArrowTooltipProps} from './ArrowTooltip';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import fetchUser from '../../utils/gaia/fetchUser';
import useSyncPromise from '../../utils/react/useSyncPromise';
import theme from '../../theme';
import trackAnalytics from '../../utils/gaia/trackAnalytics';

export interface SubscriptionTooltipProps extends Partial<ArrowTooltipProps> {
    subscription?: 'user' | 'member' | 'gaiagreen';
    children: ArrowTooltipProps['children'];
}

export default function SubscriptionTooltip(
    {subscription, title, ...restProps}: SubscriptionTooltipProps
) {
    const [user] = useSyncPromise(fetchUser());

    if (subscription === 'user' && user && !user.isAuthenticated) {
        return <Inner
            subscriptionHref="/register/"
            subscriptionTitle="Sign up for a free account to use this feature"
            title={title}
            {...restProps}
        />;

    } else if (subscription === 'member' && user && !user.hasSubscription) {
        return <Inner
            subscriptionHref="/membership/"
            subscriptionTitle="Become a Member to use this feature"
            title={title}
            {...restProps}
        />;

    } else if (subscription === 'gaiagreen' && user && !user.hasPremiumSubscription) {
        return <Inner
            subscriptionHref="/premium/"
            subscriptionTitle="Become a Premium Member to use this feature"
            title={title}
            {...restProps}
        />;

    } else if (title) {
        return <ArrowTooltip
            title={<div style={{padding: theme.spacing(1)}}>{title}</div>}
            {...restProps}
        />;

    } else {
        return restProps.children;
    }
}

interface InnerProps extends ArrowTooltipProps {
    subscriptionTitle: string;
    subscriptionHref: string;
    subscription?: void;
}

const buttonStyle = {
    fontSize: 12,
    color: '#fff',
    marginTop: theme.spacing(1),
};

function Inner({subscriptionTitle, subscriptionHref, title, ...restProps}: InnerProps) {
    const [user] = useSyncPromise(fetchUser());

    return <ArrowTooltip
        {...restProps}
        interactive
        leaveDelay={100}
        title={<Typography
            component="div"
            variant="body1"
            color="inherit"
            style={{padding: theme.spacing(1)}}
        >
            {subscriptionTitle}
            <div>{user && user.isAuthenticated ?
                <Button
                    style={buttonStyle}
                    variant="contained"
                    color="primary"
                    href={`${subscriptionHref}?next=${window.location.pathname}`}
                    onClick={() => trackAnalytics('Subscription Tooltip Upgrade', {
                        category: 'map2'
                    })}
                >
                    Upgrade
                </Button> :
                <>
                    <Button
                        style={{
                            ...buttonStyle,
                            color: theme.palette.primary.main,
                            marginRight: theme.spacing(1)
                        }}
                        variant="outlined"
                        color="primary"
                        href={`/login?next=${window.location.pathname}`}
                        onClick={() => trackAnalytics('Subscription Tooltip Log In', {
                            category: 'map2'
                        })}
                    >
                        Log In
                    </Button>
                    <Button
                        style={buttonStyle}
                        variant="contained"
                        color="primary"
                        href={`${subscriptionHref}?next=${window.location.pathname}`}
                        onClick={() => trackAnalytics('Subscription Tooltip Sign Up', {
                            category: 'map2'
                        })}
                    >
                        Sign Up
                    </Button>
                </>
            }</div>

            {title && <div style={{opacity: 0.5}}>
                <hr style={{
                    marginTop: theme.spacing(1.5),
                    marginBottom: theme.spacing(1),
                    marginLeft: 0,
                    marginRight: 0
                }} />
                {title}
            </div>}
        </Typography>}
    />;
}
